import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import type { Country } from '../types/country';

interface CountryContextType {
    countries: Country[];
    activeCountry: Country | null;
    setActiveCountry: (country: Country) => void;
    isLoading: boolean;
}

const CountryContext = createContext<CountryContextType>({
    countries: [],
    activeCountry: null,
    setActiveCountry: () => {},
    isLoading: true,
});

export const useCountry = () => useContext(CountryContext);

export const CountryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [countries, setCountries] = useState<Country[]>([]);
    const [activeCountry, setActiveCountryState] = useState<Country | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    
    const location = useLocation();
    const navigate = useNavigate();

    // 1. Fetch Countries
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await api.get('/api/v1/countries/');
                if (Array.isArray(res.data) && res.data.length > 0) {
                    setCountries(res.data);
                } else {
                    // Fallback to hardcoded Egypt if API returns empty/invalid
                    setCountries([{
                        id: 1,
                        name: "Egypt",
                        code: "EG",
                        currency_code: "EGP",
                        currency_symbol: "EGP",
                        is_active: true,
                        phone_code: "+20", 
                        measurement_unit: "sqm"
                    }]);
                }
            } catch (err) {
                console.error("Failed to fetch countries", err);
                // Fallback on error too
                setCountries([{
                    id: 1,
                    name: "Egypt",
                    code: "EG",
                    currency_code: "EGP",
                    currency_symbol: "EGP",
                    is_active: true,
                    phone_code: "+20",
                    measurement_unit: "sqm"
                }]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCountries();
    }, []);

    // 2. Sync URL <-> State
    useEffect(() => {
        if (countries.length === 0) return;

        const pathSegments = location.pathname.split('/');
        // Assuming URL is /eg/... or /qa/... (case insensitive usually, but let's assume lower in URL)
        const urlCode = pathSegments[1]?.toUpperCase(); 

        const matchedCountry = countries.find(c => c.code === urlCode);

        if (matchedCountry) {
            // URL dictates country
            if (activeCountry?.id !== matchedCountry.id) {
                setActiveCountryState(matchedCountry);
                localStorage.setItem('active_country_id', matchedCountry.id.toString());
            }
        } else {
            // URL has no country (e.g. "/" or "/admin"). 
            // Ensure state is at least valid from storage or default.
            if (!activeCountry) {
                const storedId = localStorage.getItem('active_country_id');
                const defaultCountry = countries.find(c => c.code === 'EG') || countries[0];
                const fallback = storedId ? countries.find(c => c.id === parseInt(storedId)) : defaultCountry;
                setActiveCountryState(fallback || defaultCountry);
            }
        }
    }, [location.pathname, countries]); // activeCountry omitted to avoid cycles, using check inside.

    // 3. Switcher Logic
    const handleSetCountry = (country: Country) => {
        // When user explicitly switches country, we:
        // 1. Update State
        setActiveCountryState(country);
        localStorage.setItem('active_country_id', country.id.toString());
        // 2. Navigate to that country's homepage (Reset Strategy)
        navigate(`/${country.code.toLowerCase()}`);
    };

    return (
        <CountryContext.Provider value={{ countries, activeCountry, setActiveCountry: handleSetCountry, isLoading }}>
            {children}
        </CountryContext.Provider>
    );
};
